/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { HOSPITAL_CONFIGURATION_INITIAL_STATE } from '../constants/hospitalConfiguration.constants';

export const getHospitalConfiguration = createAsyncThunk(
    'hospitalConfiguration/get',
    async (facilityId: string) => {
        const response = await axios.post(
            `/Facility/ipd/master/GetAll?FacilityId=${facilityId}`,
        );
        return response.data;
    },
);

export const saveHospitalConfiguration = createAsyncThunk(
    'hospitalConfiguration/save',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.post('/Facility/ipd/master/save', data);
            const body = response.data as {
                responseCode?: string | number;
                responseMessage?: string;
                result?: unknown;
            };
            // Return the full response so components can check responseCode and show responseMessage
            return body;
        } catch (err: unknown) {
            const errObj = err as {
                response?: {
                    data?: { responseMessage?: string };
                    status?: number;
                };
                message?: string;
            };
            const responseData = errObj.response?.data;
            const message =
                responseData?.responseMessage ??
                errObj.message ??
                'Failed to save Facility Configuration.';
            return rejectWithValue({
                responseCode: errObj.response?.status ?? 500,
                responseMessage: message,
                result: null,
            });
        }
    },
);

export const getBuildingsByFacility = createAsyncThunk(
    'hospitalConfiguration/getBuildingsByFacility',
    async (facilityId: string) => {
        const response = await axios.get(
            `/Facility/GetBuildingsByFacility/${facilityId}`,
        );
        return response.data;
    },
);

export const getWingsByBuilding = createAsyncThunk(
    'hospitalConfiguration/getWingsByBuilding',
    async (buildingId: number) => {
        const response = await axios.get(
            `/Facility/GetWingsByBuilding/${buildingId}`,
        );
        return response.data;
    },
);

export const getFloorsByWing = createAsyncThunk(
    'hospitalConfiguration/getFloorsByWing',
    async (wingId: number) => {
        const response = await axios.get(`/Facility/GetFloorsByWing/${wingId}`);
        return response.data;
    },
);

export const getRoomsByFloor = createAsyncThunk(
    'hospitalConfiguration/getRoomsByFloor',
    async (floorId: number) => {
        const response = await axios.get(
            `/Facility/GetRoomsByFloor/${floorId}`,
        );
        return response.data;
    },
);

export const getWardsByFloor = createAsyncThunk(
    'hospitalConfiguration/getWardsByFloor',
    async (floorId: number) => {
        const response = await axios.get(
            `/Facility/GetWardsByFloor/${floorId}`,
        );
        return response.data;
    },
);

export const getBedsByRoom = createAsyncThunk(
    'hospitalConfiguration/getBedsByRoom',
    async (roomId: number) => {
        const response = await axios.get(`/Facility/GetBedsByRoom/${roomId}`);
        return response.data;
    },
);

export const getBedsByWard = createAsyncThunk(
    'hospitalConfiguration/getBedsByWard',
    async (wardId: number) => {
        const response = await axios.get(`/Facility/GetBedsByWard/${wardId}`);
        return response.data;
    },
);

export interface IAdmittedPatientSearchParams {
    searchText: string;
    isDescending: boolean;
    sortingOn: string;
    perPage: number;
    pageNumber: number;
    /** FacilityProfile.Id — required for SystemAdmin/SuperAdmin facility filter */
    facilityId?: string | number;
}

export const searchAdmittedPatients = createAsyncThunk(
    'hospitalConfiguration/searchAdmittedPatients',
    async (params: IAdmittedPatientSearchParams) => {
        const { facilityId, ...rest } = params;
        const payload: Record<string, unknown> = { ...rest };
        if (facilityId != null && String(facilityId).trim() !== '') {
            payload.facilityId = String(facilityId);
        }
        const response = await axios.post(
            '/IPDAdmission/AdmittedPatinet/Search',
            payload,
        );
        return response.data;
    },
);

export const hospitalConfigurationSlice = createSlice({
    name: 'hospitalConfiguration',
    initialState: HOSPITAL_CONFIGURATION_INITIAL_STATE,
    reducers: {
        setSelectedFacilityId: (state, action) => {
            state.selectedFacilityId = action.payload;
        },
        clearHospitalConfiguration: (state) => {
            state.data = [];
            state.buildings = [];
            state.wings = [];
            state.floors = [];
            state.rooms = [];
            state.wards = [];
            state.beds = [];
            state.bedsByWard = [];
            state.admittedPatients = [];
            state.admittedPatientsLoading = 'idle';
            state.selectedFacilityId = null;
            state.error = null;
        },
        clearAdmittedPatients: (state) => {
            state.admittedPatients = [];
            state.admittedPatientsTotalRecords = 0;
            state.admittedPatientsTotalPages = 0;
            state.admittedPatientsLoading = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getHospitalConfiguration.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getHospitalConfiguration.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getHospitalConfiguration.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ??
                    'Failed to fetch Facility Configuration.';
            })
            .addCase(saveHospitalConfiguration.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(saveHospitalConfiguration.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(saveHospitalConfiguration.rejected, (state, action) => {
                state.loading = 'failed';
                const payload = action.payload as
                    | { responseMessage?: string }
                    | undefined;
                state.error =
                    payload?.responseMessage ??
                    action.error.message ??
                    'Failed to save Facility Configuration.';
            })
            .addCase(getBuildingsByFacility.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getBuildingsByFacility.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.buildings = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getBuildingsByFacility.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ??
                    'Failed to fetch buildings by facility.';
            })
            .addCase(getWingsByBuilding.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getWingsByBuilding.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.wings = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getWingsByBuilding.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ??
                    'Failed to fetch wings by building.';
            })
            .addCase(getFloorsByWing.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getFloorsByWing.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.floors = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getFloorsByWing.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ?? 'Failed to fetch floors by wing.';
            })
            .addCase(getRoomsByFloor.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getRoomsByFloor.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.rooms = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getRoomsByFloor.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ?? 'Failed to fetch rooms by floor.';
            })
            .addCase(getWardsByFloor.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getWardsByFloor.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.wards = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getWardsByFloor.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ?? 'Failed to fetch wards by floor.';
            })
            .addCase(getBedsByRoom.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getBedsByRoom.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.beds = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getBedsByRoom.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ?? 'Failed to fetch beds by room.';
            })
            .addCase(getBedsByWard.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(getBedsByWard.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.bedsByWard = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getBedsByWard.rejected, (state, action) => {
                state.loading = 'failed';
                state.error =
                    action.error.message ?? 'Failed to fetch beds by ward.';
            })
            .addCase(searchAdmittedPatients.pending, (state) => {
                state.admittedPatientsLoading = 'pending';
            })
            .addCase(searchAdmittedPatients.fulfilled, (state, action) => {
                state.admittedPatientsLoading = 'succeeded';
                const result = action.payload?.result || {};
                state.admittedPatients = result.list || [];
                state.admittedPatientsTotalRecords = result.totalRecords ?? 0;
                state.admittedPatientsTotalPages = result.totalPages ?? 0;
            })
            .addCase(searchAdmittedPatients.rejected, (state, action) => {
                state.admittedPatientsLoading = 'failed';
                state.error =
                    action.error.message ??
                    'Failed to search admitted patients.';
            });
    },
});

export const {
    setSelectedFacilityId,
    clearHospitalConfiguration,
    clearAdmittedPatients,
} = hospitalConfigurationSlice.actions;

export default hospitalConfigurationSlice.reducer;
